/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import { TrackedArray } from 'tracked-built-ins';
import { computed } from '@ember/object';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { getGroupVisibilityFilter } from '../utils/groups';

const TYPE_MAP = {
  classes: GroupType.HOMEROOM,
  groups: GroupType.GROUP,
  shared: GroupType.SHARED,
  autogroups: GroupType.AUTO,
};

export default class ReportingGroupBrowserService extends Service {
  @service
  session;

  @service
  store;

  // Filter/search state — shared between the sidebar and the hub menu
  @tracked selectedGroupType = 'classes';

  @tracked groupSearch = '';
  @tracked
  expandedGroups = null;

  // Set by the reporting route's setupController once groups are loaded
  @tracked
  groups = null;

  // Tracks which groups we've already fetched learner persons for, so we
  // don't re-hit the API every time a user toggles expansion.
  @tracked
  _loadedPersonsForGroupIds = null;

  init() {
    super.init(...arguments);
    // expandedGroups is a TrackedArray, not a plain array: components/
    // hub/menu.js and controllers/reporting/{index,group}.js call
    // push/splice directly on it, and need that mutation to be
    // independently reactive without reassigning this field each time.
    this.expandedGroups = new TrackedArray();
    this.groups = [];
    this._loadedPersonsForGroupIds = new Set();
  }

  ensureGroupPersonsLoaded(groupId) {
    if (!groupId) return Promise.resolve();
    let loaded = this._loadedPersonsForGroupIds;
    if (loaded.has(groupId)) return Promise.resolve();
    loaded.add(groupId);
    return this.store
      .query('person', {
        type: PersonType.LEARNER,
        groupId,
        includeFormer: true,
      })
      .then(() => {
        // group-member fragments cache `person` keyed only on `personId`, so
        // fragments that peeked before the record was loaded keep a stale
        // null. Invalidate those caches and the group's filtered-learners
        // computed so the newly-loaded persons appear in the sidebar.
        let group = this.store.peekRecord('group', groupId);
        if (!group) return;
        let learners = group.get('learners');
        if (learners && learners.forEach) {
          learners.forEach((l) => l.notifyPropertyChange('person'));
        }
        group.notifyPropertyChange('filteredLearners');
      })
      .catch(() => {
        loaded.delete(groupId);
      });
  }

  @computed('groups.@each.currentLearners', 'session.personId')
  get visibleGroups() {
    let groups = this.groups || [];
    let visibilityFilter = getGroupVisibilityFilter(this.session.personId);
    return groups.filter((group) => {
      if (!visibilityFilter(group)) return false;
      let currentLearners = group.get('currentLearners');
      return currentLearners && currentLearners.length > 0;
    });
  }

  @computed(
    'selectedGroupType',
    'groupSearch',
    'visibleGroups.@each.{type,label,name}',
  )
  get filteredGroups() {
    let allGroups = this.visibleGroups || [];
    let search = (this.groupSearch || '').toLowerCase().trim();

    let filtered;
    if (search) {
      filtered = allGroups.filter((group) => {
        let label = (group.get('label') || '').toLowerCase();
        let name = (group.get('name') || '').toLowerCase();
        return label.includes(search) || name.includes(search);
      });
    } else {
      let groupType = TYPE_MAP[this.selectedGroupType];
      filtered = groupType
        ? allGroups.filter((g) => g.get('type') === groupType)
        : allGroups;
    }

    return filtered.slice().sort(naturalSort('name'));
  }
}
